import { Injectable, inject } from '@angular/core';
import { NotificationModel } from '../models/notification-model';
import { FollowAggService } from '@shared/service/http_services/follow-agg.service';
import { FollowsService } from '@shared/service/http_services/follows.service';
import { BehaviorSubject, filter, finalize, map, Observable, of, Subject, switchMap, tap } from 'rxjs';
import { IGetFollowRequest } from '@shared/model/followModels/follow-request.model';
import { TermFilterModel } from '@shared/model/categoryAndSearch/term-filter-model';
import { AppStateService } from '@app/state/app-state.service';
import { ActivitiesAggrigatorService } from '@shared/service/http_services/activitiesAggrigator.service';
import { ActivityVerbEnum } from '@shared/model/enum/activity-verb-enum';
import { ActivitiesUpdateService } from '@shared/service/http_services/activitiesUpdate.service';
import { Router } from '@angular/router';
import { SnackBarTypes } from '@shared/components/cutome-snack-bar/custome-snack-bar.component';
import { LocalRepository } from '@shared/helper/local-repository';
import { NOTIFICATION_SCROLL_POSITION_KEY } from 'constants/storage-keys';
import { SnackbarService } from '@shared/utils/snackbar.service';

@Injectable({
  providedIn: 'root'
})
export class NotificationFacadeService {
  private followAggService = inject(FollowAggService);
  private followService = inject(FollowsService);
  private activitiesAggrigatorService = inject(ActivitiesAggrigatorService);
  private activitiesUpdateService = inject(ActivitiesUpdateService);
  private router = inject(Router);
  private appStateService = inject(AppStateService);

  private repo = inject(LocalRepository);
  private snackbarService = inject(SnackbarService);


  private _notifications: BehaviorSubject<NotificationModel[]> = new BehaviorSubject<NotificationModel[]>([]);
  private _todayNotifications: BehaviorSubject<NotificationModel[]> = new BehaviorSubject<NotificationModel[]>([]);
  private _yesterdayNotifications: BehaviorSubject<NotificationModel[]> = new BehaviorSubject<NotificationModel[]>([]);
  private _thisWeekNotifications: BehaviorSubject<NotificationModel[]> = new BehaviorSubject<NotificationModel[]>([]);
  public followRequestData$ = new Observable<IGetFollowRequest[]>();
  private _newNotificationsAvailable$ = new BehaviorSubject<boolean>(false);
  private _onTransitionDone$ = new Subject<void>();

  followRequestTermFilter: TermFilterModel = new TermFilterModel(0, 2);
  notificationTermFilter: TermFilterModel = new TermFilterModel(0, 20);
  loading = false;
  notificationCount = 0;
  hasMoreNotification = false;
  unseenNotificationsCount = 0;
  loadingMore!: boolean;

  public get todayNotifications() {
    return this._todayNotifications.asObservable();
  }
  public get yesterdayNotifications() {
    return this._yesterdayNotifications.asObservable();
  }
  public get thisWeekNotifications() {
    return this._thisWeekNotifications.asObservable();
  }
  public get notifications() {
    return this._notifications.asObservable();
  }
  public get newNotificationsAvailable$() {
    return this._newNotificationsAvailable$.asObservable();
  }
  public get onTransitionDone$(): Observable<void> {
    return this._onTransitionDone$.asObservable();
  }


  constructor() {
    this.followRequestData$ = this.getFollowRequests();
  }


  public getFollowRequests() {
    return this.followAggService.getFollowRequests(this.followRequestTermFilter).pipe(
      filter(res => !res.hasError),
      map(res => {
        return res.result.items;
      })
    );
  }

  public handleNotificationSignalR() {
    return this.appStateService.hasNewNotification$.pipe(
      switchMap(res => {
        if (res)
          this._newNotificationsAvailable$.next(true);
        return of(null);
      })
    );
  }


  public getNotifications(useCache = false) {
    this.loading = true;
    if (useCache) {
      if (this._todayNotifications.value.length > 0 || this._yesterdayNotifications.value.length > 0 || this._thisWeekNotifications.value.length || this._notifications.value.length > 0) {
        this.loading = false;
        return of(null);
      }
    }
    this._newNotificationsAvailable$.next(false);
    return this.activitiesAggrigatorService.getMyNotifications(this.notificationTermFilter).pipe(
      switchMap(({ hasError, result }) => {
        this.loading = false;
        if (hasError || !result) return of(null);
        this.notificationCount = result.totalCount;
        this.hasMoreNotification = result?.hasNextPage;
        this.categorizeNotifications(result?.items, useCache);
        this.appStateService.notificationCountModifier$ = 0;
        return this.setSeenNotification();
      })
    );

  }

  public loadMoreNotifications(): Observable<any> {
    if (!this.hasMoreNotification || !this.router.url.includes('notifications')) return of(null);
    this.loadingMore = true;
    this.notificationTermFilter.pgNumber++;
    return this.activitiesAggrigatorService.getMyNotifications(this.notificationTermFilter)
      .pipe(
        finalize(() => this.loadingMore = false),
        tap(({ hasError, result }) => {
          if (hasError || !result) return;
          this.categorizeNotifications(result?.items, true);
          this.hasMoreNotification = result?.hasNextPage;
        })
      );
  }


  private categorizeNotifications(notifications: NotificationModel[], useCache?: boolean) {

    const todayActivities: NotificationModel[] = useCache ? this._todayNotifications.value : [];
    const yesterdayActivities: NotificationModel[] = useCache ? this._yesterdayNotifications.value : [];
    const thisWeekActivities: NotificationModel[] = useCache ? this._thisWeekNotifications.value : [];
    const restActivities: NotificationModel[] = useCache ? this._notifications.value : [];
    const now = new Date().getTime();


    for (const element of notifications) {
       const notif = element;
      // ? rejected follow request
      if ((notif.activity?.inDirectObject == null && notif.activity?.verb == ActivityVerbEnum.FollowRequested) ||
        (notif.activity?.inDirectObject == null && notif.activity?.verb === ActivityVerbEnum.Meetinged)) {
        continue;
      }
      const notifTime = new Date(Date.parse(notif.activity?.createdAt!)).getTime();
      const timeRange = now - notifTime;
      const minutes = Math.floor((timeRange / 1000 / 60));
      const hours = Math.floor(minutes / 60);
      const days = Math.floor(hours / 24);
      const weeks = Math.floor(days / 7);
      const isToday = (days == 0 && !(hours >= 24)) || timeRange < 0;
      const isYesterday = (hours >= 24) && (days) == 1;
      const isThisWeek = (days > 7) && weeks == 0;

      if (isToday) {
        if (useCache) {
          // ? unshift when we connot find notification
          const notifExists = todayActivities.some(n => n.id === notif.id);
          if (!notifExists)
            todayActivities.push(notif);
        }
        else
          todayActivities.push(notif);
      }
      else if (isYesterday)
        yesterdayActivities.push(notif);
      else if (isThisWeek)
        thisWeekActivities.push(notif)
      else
        restActivities.push(notif);

      this._todayNotifications.next(todayActivities);
      this._yesterdayNotifications.next(yesterdayActivities);
      this._thisWeekNotifications.next(thisWeekActivities);
      this._notifications.next(restActivities);
    }
  }

  public setSeenNotification() {
    return this.activitiesUpdateService.setSeenAllNotifications()
      .pipe(
        tap(({ hasError }) => {
          if (hasError) return;
          this.unseenNotificationsCount = 0;
        this.appStateService.notificationCountModifier$ = 0;
        })
      );
  }


  public acceptFollowRequest(activity: NotificationModel) {
    return this.followService.acceptFollowRequest(activity.activity?.actor?.id!).pipe(
      tap(res => {
        if (!res.hasError) {
          this.removeItemFromList(activity);
        }
        else
          this.snackbarService.openSnackbar(SnackBarTypes.error, 'عملیات با خطا مواجه شد' );
      })
    );
  }

  public rejectFollowRequest(activity: NotificationModel) {
    return this.followService.rejectFollowRequest(activity?.activity?.actor?.id!).pipe(
      tap(res => {
        if (!res.hasError) {
          this.removeItemFromList(activity);
        }
        else
          this.snackbarService.openSnackbar(SnackBarTypes.error, 'عملیات با خطا مواجه شد' );
      })
    );
  }

  public removeItemFromList(req: NotificationModel): void {
    const notifList = this._notifications.value;
    const index = notifList.indexOf(req);
    notifList.splice(index, 1);
    this._notifications.next(notifList);
  }

  public setScrollPosition(position: number): void {
    if (this.repo.IsInBrowser) {
      this.repo.setLocalItem(NOTIFICATION_SCROLL_POSITION_KEY, position.toString());
    }
  }

  public getScrollPosition(): number | null {
    if (this.repo.IsInBrowser) {
      return parseInt(this.repo.getLocalItem(NOTIFICATION_SCROLL_POSITION_KEY)!, 10) ?? null;
    }
    return null;
  }

  public scrollToTop(): void {
    if (this.repo.IsInBrowser)
      window.scrollTo(0, 0);
  }


  public clearNotificationCache() {
    this._newNotificationsAvailable$.next(false);
    this._todayNotifications.next([]);
    this._yesterdayNotifications.next([]);
    this._thisWeekNotifications.next([]);
    this._notifications.next([]);
  }

  public runSetTimeout() {
    this._onTransitionDone$.next();
  }


}
